import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { api } from '../../../../services/apiService';
import { ListBulletIcon } from '@heroicons/react/24/outline';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const SpendingByTypeChart = ({ dataView = 'combined', isExpanded = false }) => {
  const [typeData, setTypeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLegend, setShowLegend] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  
  // Track window size for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      // On desktop, always show legend; on mobile, respect showLegend state
      if (window.innerWidth >= 1024) {
        setShowLegend(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Sync legend visibility with card expansion on mobile
  useEffect(() => {
    if (isExpanded) {
      setShowLegend(true);
    } else {
      // Only hide on mobile (when window width < 1024px)
      if (!isDesktop) {
        setShowLegend(false);
      }
    }
  }, [isExpanded, isDesktop]);

  useEffect(() => {
    const fetchTypeData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/dashboard/transactions-by-type', {
          params: { dataView }
        });
        setTypeData(response.data || []);
      } catch (err) {
        console.error('Error fetching type breakdown:', err);
        setError('Failed to load type data');
      } finally {
        setLoading(false);
      }
    };

    fetchTypeData();
  }, [dataView]);

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="text-gray-500">Loading type data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!typeData || typeData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="text-gray-500">No type data available</div>
      </div>
    );
  }

  // Color scheme for transaction types
  const colors = [
    '#EF4444', // Red-500 for Bills
    '#F59E0B', // Amber-500 for Expenses
    '#3B82F6', // Blue-500 for Transfers
    '#10B981', // Emerald-500 for Payments
    '#8B5CF6', // Violet-500 for Income
    '#EC4899', // Pink-500 for Other
  ];

  const chartData = {
    labels: typeData.map(item => item.type),
    datasets: [{
      data: typeData.map(item => item.amount),
      backgroundColor: colors.slice(0, typeData.length),
      borderColor: colors.slice(0, typeData.length).map(color => color),
      borderWidth: 2,
      // Explicitly disable any labels on the dataset
      datalabels: {
        display: false
      }
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: isExpanded ? 20 : 8
    },
    elements: {
      arc: {
        borderWidth: 0
      }
    },
    plugins: {
      legend: {
        display: isDesktop || showLegend, // Always show on desktop (>=1024px), conditional on mobile
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: isExpanded ? 15 : 8,
          font: {
            size: isExpanded ? 12 : 9,
            family: "'Inter', 'system-ui', sans-serif"
          },
          boxWidth: isExpanded ? 12 : 8,
          boxHeight: isExpanded ? 12 : 8
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context) {
            const value = Math.abs(context.parsed);
            return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }
      },
      datalabels: {
        display: false
      }
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 relative min-h-0">
        <Pie data={chartData} options={chartOptions} />
      </div>
      {/* Legend Toggle Button - Only visible on mobile, centered below chart */}
      <div className="lg:hidden flex justify-center mt-0.5">
        <button
          onClick={() => setShowLegend(!showLegend)}
          className={`flex items-center gap-0.5 text-[7px] leading-none px-0.5 py-0 rounded transition-colors ${
            showLegend 
              ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          style={{ height: 'fit-content', minHeight: 'auto' }}
          aria-label={showLegend ? 'Hide legend' : 'Show legend'}
        >
          <ListBulletIcon className="h-2 w-2" />
          <span>{showLegend ? 'Hide' : 'Show'} Legend</span>
        </button>
      </div>
    </div>
  );
};

export default SpendingByTypeChart; 